import { useContext, type PropsWithChildren } from "react";
import {
  Collection,
  Tabs as TabsPrimitive,
  type Key,
} from "react-aria-components";
import {
  CoreButtonTabs,
  CoreButtonAlt,
  type PropsWithOptionalTestID,
} from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./ButtonTabs.styled";
import ButtonTabsContext, { ButtonTabsProvider } from "./ButtonTabsContext";
import { Typography } from "../Typography";

export type ButtonTabsProps = PropsWithOptionalTestID &
  PropsWithChildren<{
    variant?: CoreButtonTabs.Variant;
    typographyVariant?: CoreButtonAlt.TypographyVariant;
    items: CoreButtonTabs.Items;
    activeItemId: CoreButtonTabs.Item["id"];
    onChange: (activeItemId: CoreButtonTabs.Item["id"]) => void;
  }>;

function ButtonTabs(props: ButtonTabsProps) {
  const { children, testID = "button-tabs", ...otherProps } = props;
  const { activeItemId, onChange } = otherProps;

  function handleSelectionChange(key: Key) {
    onChange(key as CoreButtonTabs.Item["id"]);
  }

  return (
    <ButtonTabsProvider {...otherProps} testID={testID}>
      <TabsPrimitive
        selectedKey={activeItemId}
        onSelectionChange={handleSelectionChange}
        data-testid={testID}
      >
        {children}
      </TabsPrimitive>
    </ButtonTabsProvider>
  );
}

ButtonTabs.Items = function ButtonTabsList() {
  const {
    variant = CoreButtonTabs.DEFAULT_VARIANT,
    items,
    typographyVariant,
    testID,
  } = useContext(ButtonTabsContext);

  const { tokens } = useDesignTokens();
  const { buttonAlt } = tokens;

  return (
    <S.TabList $variant={variant} data-testid={`${testID}-items`}>
      <Collection key={`${variant}-${typographyVariant}`} items={items}>
        {(item) => (
          <S.Tab
            id={item.id}
            $fullWidth={variant === "stretch"}
            data-testid={`${testID}-item-${item.id}`}
          >
            <Typography
              color="inherit"
              variant={typographyVariant}
              component="span"
              fontFamily={buttonAlt.typography.fontFamily}
            >
              {item.label}
            </Typography>
          </S.Tab>
        )}
      </Collection>
    </S.TabList>
  );
};

ButtonTabs.Panels = function ButtonTabsPanels() {
  const { items, testID } = useContext(ButtonTabsContext);

  return (
    <div data-testid={`${testID}-panels`}>
      <Collection items={items}>
        {(item) => (
          <S.TabPanel
            key={item.id}
            id={item.id}
            data-testid={`${testID}-panel-${item.id}`}
          >
            {item.panel}
          </S.TabPanel>
        )}
      </Collection>
    </div>
  );
};

export default ButtonTabs;
